import DeleteIcon from "@mui/icons-material/Delete";
import { Checkbox, Fade, IconButton, TableCell, TableRow } from "@mui/material";
import React, {
  type Dispatch,
  type SetStateAction,
  useMemo,
  useState,
} from "react";
import {
  SchemaCompanyMappingsDocument,
  type SchemaCompanyMappingsQuery,
  SearchSchemasDocument,
  useCompanyContactsQuery,
  useDeleteSchemaCompanyMappingMutation,
  useUpdateSchemaCompanyMappingMutation,
} from "../../../../generated/graphql";
import CompanySelect from "../../company-select";
import TerminalSelect from "../../terminal-select";
import useSchemaStore from "../schema-store";

const SchemaCompanyMappingRow = ({
  schemaCompanyMapping,
  selectedSchemaCompanyMappingId,
  setSelectedSchemaCompanyMappingId,
}: {
  readonly schemaCompanyMapping: SchemaCompanyMappingsQuery["schemaCompanyMappings"][0];
  readonly selectedSchemaCompanyMappingId: string | undefined;
  readonly setSelectedSchemaCompanyMappingId: Dispatch<
    SetStateAction<string | undefined>
  >;
}) => {
  const deselectAllSchemaDocuments = useSchemaStore(
    (state) => state.deselectAllSchemaDocuments,
  );
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [updateSchemaCompanyMapping] = useUpdateSchemaCompanyMappingMutation({
    refetchQueries: [SchemaCompanyMappingsDocument, SearchSchemasDocument],
  });
  const [deleteSchemaCompanyMapping] = useDeleteSchemaCompanyMappingMutation({
    refetchQueries: [SchemaCompanyMappingsDocument, SearchSchemasDocument],
  });
  // A mapping generally belongs to a company other than the operator's, and
  // `contact(uuid:)` is scoped to the caller, so it cannot read this one.
  // `companyContacts` is the cross-company reader for exactly this case and is
  // gated by `@InternalTools()`. Apollo dedupes it per `companyUuid`, so rows
  // sharing a company share one request with the contact pickers on this
  // screen rather than issuing a lookup each.
  const { data: contactsData } = useCompanyContactsQuery({
    variables: {
      companyUuid: schemaCompanyMapping.companyUuid,
    },
  });
  const contactDisplayName = useMemo(
    () =>
      contactsData?.companyContacts.find(
        (contact) => contact.uuid === schemaCompanyMapping.contactUuid,
      )?.displayName,
    [contactsData?.companyContacts, schemaCompanyMapping.contactUuid],
  );

  const onDelete = async () => {
    await deleteSchemaCompanyMapping({
      variables: {
        id: schemaCompanyMapping.id,
      },
    });
  };

  const onChangeTerminal = async (uuid: string | null) => {
    await updateSchemaCompanyMapping({
      variables: {
        updateSchemaCompanyMappingInput: {
          id: schemaCompanyMapping.id,
          terminalUuid: uuid,
        },
      },
    });
  };

  const onCheck = async () => {
    await updateSchemaCompanyMapping({
      variables: {
        updateSchemaCompanyMappingInput: {
          id: schemaCompanyMapping.id,
          isActive: !schemaCompanyMapping.isActive,
        },
      },
    });
  };

  const onClick = () => {
    if (schemaCompanyMapping.id !== selectedSchemaCompanyMappingId) {
      deselectAllSchemaDocuments();
      setSelectedSchemaCompanyMappingId(schemaCompanyMapping.id);
    }
  };

  return (
    <TableRow
      sx={{
        backgroundColor:
          selectedSchemaCompanyMappingId === schemaCompanyMapping.id ||
          isHovered
            ? "#f0f0f0"
            : undefined,
        cursor: "pointer",
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onClick={() => {
        onClick();
      }}
    >
      <TableCell>
        <CompanySelect
          isText
          currentCompanyUuid={schemaCompanyMapping.companyUuid}
          onChange={() => {}}
        />
      </TableCell>
      <TableCell>{contactDisplayName}</TableCell>
      <TableCell sx={{ width: 175 }}>
        <TerminalSelect
          companyUuid={schemaCompanyMapping.companyUuid}
          currentTerminalUuid={schemaCompanyMapping.terminalUuid}
          setTerminalUuid={onChangeTerminal}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={schemaCompanyMapping.isActive}
          onChange={(e) => {
            e.stopPropagation();
            onCheck();
          }}
        />
      </TableCell>
      <TableCell>
        <Fade in={isHovered}>
          <IconButton sx={{ float: "right" }} onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Fade>
      </TableCell>
    </TableRow>
  );
};
export default React.memo(SchemaCompanyMappingRow);
